// MARCOS GEODÉSICOS — acervo web (MG-3/4/5/6, 2026-07-20). Aba do hub Campo.
// Mapa MapLibre (todos os marcos, pins por status) + tabela + Ficha (Drawer).
// Fluxos: importar monografia pretérita (XLSX parse local / PDF via IA),
// consolidar com PPP-IBGE (IA + diff + aplicar), validar, gerar monografia
// (PDF no pdf-generator; XLSX client-side via SheetJS).
// Backend: mobile-engine /mobile/marcos* (greedy — sem API GW novo).
import { useEffect, useMemo, useRef, useState } from 'react';
import maplibregl, { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import axios from 'axios';
import {
  Alert, App as AntApp, Button, Col, Collapse, DatePicker, Descriptions, Drawer,
  Empty, Form, Input, InputNumber, Modal, Popconfirm, Row, Segmented, Select,
  Space, Spin, Table, Tag, Tooltip, Typography, Upload,
} from 'antd';
import {
  CloudUploadOutlined, DeleteOutlined, DownloadOutlined, EditOutlined,
  EnvironmentOutlined, FileExcelOutlined, FilePdfOutlined,
  ReloadOutlined, SafetyCertificateOutlined, AimOutlined,
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs, { Dayjs } from 'dayjs';
import { usePerms } from '../hooks/usePerms';
import { API_URL } from '../config';
import { getToken } from '../utils/auth';

const { Text, Title } = Typography;

// ── Tipos ──────────────────────────────────────────────────────────────────
interface MarcoLista {
  id: string; codigo: string; tipo: string; status: string;
  contrato_id: string | null; contrato_numero: string | null;
  municipio: string | null; uf: string | null; data_implantacao: string | null;
  lat: string | number | null; lon: string | number | null;
  equipamento: string | null; criado_por: string | null; criado_em: string;
  fonte: string | null; incerteza_m: string | null; final_alt_normal_m: string | null;
}
interface Anexo {
  tipo: string; s3_key: string; nome: string | null; content_type: string | null;
  tamanho_bytes: number | null; origem: string; subido_por: string; subido_em: string;
  url?: string | null;
}
interface FotoMarco { id: number; s3_key: string; url: string; lat: string | null; lon: string | null; capturado_em: string | null; descricao: string | null }
interface ContratoOpt { id: string; numero: string | null; titulo: string | null }

const STATUS_INFO: Record<string, { cor: string; rotulo: string }> = {
  campo: { cor: 'orange', rotulo: 'Campo' },
  processado: { cor: 'blue', rotulo: 'Processado' },
  validado: { cor: 'green', rotulo: 'Validado' },
};
const STATUS_COR_MAPA: Record<string, string> = { campo: '#f59e0b', processado: '#3b82f6', validado: '#0a7f4a' };

// ── API (mobile-engine; Authorization = idToken cru, sem Bearer) ────────────
async function mob<T = any>(metodo: 'get' | 'post' | 'patch' | 'delete', path: string, body?: any): Promise<T> {
  const token = await getToken();
  const r = await axios.request({ method: metodo, url: `${API_URL}${path}`, data: body, headers: { Authorization: token } });
  return r.data?.body ? JSON.parse(r.data.body) : r.data;
}
async function listarContratosOpts(): Promise<ContratoOpt[]> {
  const token = await getToken();
  const r = await axios.post(`${API_URL}/comercial/propostas-geradas`, { action: 'op_listar_contratos' }, { headers: { Authorization: token } });
  const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
  return Array.isArray(d) ? d : [];
}

// ── GMS → decimal (mesma regra do backend) — hemisfério por letra OU sinal
// ("-30° 13´ 59,6118˝" é o formato do PPP-IBGE) ─────────────────────────────
function gmsParaDecimal(txt?: string | null): number | null {
  if (!txt) return null;
  const t = String(txt).trim().replace(/''/g, '"');
  const m = t.match(/^([SWNEO+-])?\s*(\d+)\s*[°º]\s*(\d+)\s*['’´`]\s*([\d.,]+)/);
  if (!m) return null;
  const hem = m[1] || (/[SWNEO]$/.test(t) ? t.slice(-1) : '');
  const dec = parseInt(m[2], 10) + parseInt(m[3], 10) / 60 + parseFloat(m[4].replace(',', '.')) / 3600;
  return Math.round((['S', 'W', 'O', '-'].includes(hem) ? -dec : dec) * 1e7) / 1e7;
}
const num = (v: any): number | null => {
  if (v == null || v === '' || v === '-') return null;
  const n = parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) ? n : null;
};
const fmtN = (v: any, dig = 3) => (v == null || v === '' ? '—' : Number(v).toFixed(dig));
// DATE puro do PG chega como "YYYY-MM-DDT00:00:00.000Z" — cortar a hora antes
// do dayjs (senão o fuso -03:00 recua um dia)
const dataSo = (v: any): Dayjs | null => (v ? dayjs(String(v).slice(0, 10)) : null);

// ── Parser do XLSX de monografia (template Hydrodata; label-based) ─────────
function parseMonografiaXlsx(buf: ArrayBuffer): Record<string, any> {
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const grid: any[][] = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: true, defval: null });

  const linhaDe = (re: RegExp): number => grid.findIndex((row) => row?.some((c) => typeof c === 'string' && re.test(c.trim())));
  const rotulo = (re: RegExp, ocorrencia = 1): any => {
    let vistos = 0;
    for (const row of grid) {
      if (!row) continue;
      for (let c = 0; c < row.length; c++) {
        if (typeof row[c] === 'string' && re.test(row[c].trim())) {
          vistos += 1;
          if (vistos < ocorrencia) continue;
          for (let c2 = c + 1; c2 < row.length; c2++) {
            if (row[c2] != null && String(row[c2]).trim() !== '') return row[c2];
          }
          return null;
        }
      }
    }
    return null;
  };
  const dataIso = (v: any): string | null => {
    if (v instanceof Date && !Number.isNaN(v.getTime())) return dayjs(v).format('YYYY-MM-DD');
    const m = String(v ?? '').match(/(\d{4})-(\d{2})-(\d{2})/) || String(v ?? '').match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!m) return null;
    return m[0].includes('/') ? `${m[3]}-${m[2]}-${m[1]}` : m[0];
  };
  const hora = (v: any): string | null => {
    if (v instanceof Date) return dayjs(v).format('HH:mm:ss');
    if (typeof v === 'number' && v < 1) {
      const s = Math.round(v * 86400);
      return `${String(Math.floor(s / 3600)).padStart(2, '0')}:${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    }
    const m = String(v ?? '').match(/(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    return m ? `${m[1].padStart(2, '0')}:${m[2]}:${m[3] || '00'}` : null;
  };
  const bloco = (rIni: number, rFim: number): string | null => {
    const linhas: string[] = [];
    for (let r = rIni; r < rFim && r < grid.length; r++) {
      const l = (grid[r] || []).filter((c) => c != null && String(c).trim() !== '').map((c) => String(c).trim()).join(' ');
      if (l) linhas.push(l);
    }
    return linhas.join('\n') || null;
  };

  const d: Record<string, any> = {};
  d.codigo = rotulo(/^Nome do marco\s*:/i);
  d.data_implantacao = dataIso(rotulo(/^Data de implanta/i));
  d.municipio = rotulo(/^Mun[íi]?c[íi]pio\s*:/i);
  d.uf = rotulo(/^UF\s*:/i);
  const inter = rotulo(/^Marcos intervis/i);
  d.marcos_intervisiveis = inter && !['-', '--'].includes(String(inter).trim())
    ? String(inter).split(/[,;/]/).map((s) => s.trim()).filter(Boolean) : undefined;
  d.datum = rotulo(/^Datum\s*:/i);
  d.fuso = rotulo(/^Fuso\s*:/i);
  const mc = rotulo(/^Meridiano Central\s*:/i);
  d.meridiano_central = mc ? String(mc).replace('º', '°') : null;
  d.solucao = rotulo(/^Solu[çc][ãa]o\s*:/i);
  d.equipamento = rotulo(/^Equipamento\s*:/i);
  const base = rotulo(/^Base\s*:/i);
  d.base = base && !['-', '--'].includes(String(base).trim()) ? base : null;
  d.altura_antena_m = num(rotulo(/^Alt\.?\s*Antena/i));
  d.metodo = rotulo(/^M[ée]todo\s*:/i);
  const di = dataIso(rotulo(/^Data\s*:/i, 1)), hi = hora(rotulo(/^Hora\s*:/i, 1));
  const df = dataIso(rotulo(/^Data\s*:/i, 2)), hf = hora(rotulo(/^Hora\s*:/i, 2));
  if (di && hi) d.rastreio_inicio = `${di}T${hi}-03:00`;
  if (df && hf) d.rastreio_fim = `${df}T${hf}-03:00`;
  d.final_lat_gms = rotulo(/^Latitude\s*:/i);
  d.final_lon_gms = rotulo(/^Longitude\s*:/i);
  d.final_utm_n = num(rotulo(/^N \(m\)\s*:/i));
  d.final_utm_e = num(rotulo(/^E \(m\)\s*:/i));
  d.final_alt_geometrica_m = num(rotulo(/^Alt\.?\s*Geom[ée]trica/i));
  d.final_alt_normal_m = num(rotulo(/^Alt\.?\s*Normal/i));
  d.incerteza_m = num(String(rotulo(/^Incerteza\s*:/i) ?? '').replace('m', ''));
  d.processamento = rotulo(/^Processamento\s*:/i);
  const fonte = rotulo(/^Fonte\s*:/i);
  d.modelo_geoidal = fonte ? String(fonte).replace(/^Modelo\s+/i, '').trim() : null;
  // sigmas: menores valores (<1) nas linhas de lat/lon/alt geométrica
  const sigmaDaLinha = (r: number): number | null => {
    if (r < 0) return null;
    const cand = (grid[r] || []).map(num).filter((n): n is number => n != null && n > 0 && n < 1);
    return cand.length ? cand[0] : null;
  };
  d.sigma_lat_m = sigmaDaLinha(linhaDe(/^Latitude\s*:/i));
  d.sigma_lon_m = sigmaDaLinha(linhaDe(/^Longitude\s*:/i));
  d.sigma_alt_m = sigmaDaLinha(linhaDe(/^Alt\.?\s*Geom[ée]trica/i));
  const rDesc = linhaDe(/Descri[çc][ãa]o do marco/i), rCroqui = linhaDe(/Croqui/i);
  if (rDesc >= 0) d.descricao = bloco(rDesc + 1, rCroqui > rDesc ? rCroqui : rDesc + 8);
  const rInfo = linhaDe(/Informa[çc][õo]es complementares/i);
  if (rInfo >= 0) d.observacoes = bloco(rInfo + 1, rInfo + 8);
  const rContr = linhaDe(/^Contratante\s*:/i);
  if (rContr >= 0) d.contratante = bloco(rContr + 1, rContr + 2);
  d.tipo = String(d.codigo || '').toUpperCase().startsWith('E') ? 'ponto_apoio' : 'marco';
  d.lat = gmsParaDecimal(d.final_lat_gms);
  d.lon = gmsParaDecimal(d.final_lon_gms);
  return d;
}

// ── Export XLSX (layout monografia; sem fotos — elas vão no PDF) ────────────
function baixarMonografiaXlsx(m: any) {
  const dt = (v: any) => (v ? dayjs(v).format('DD/MM/YYYY') : '—');
  const hr = (v: any) => (v ? dayjs(v).format('HH:mm:ss') : '—');
  const dur = (i: any, f: any) => {
    if (!i || !f) return '—';
    const s = Math.floor((new Date(f).getTime() - new Date(i).getTime()) / 1000);
    if (!Number.isFinite(s) || s <= 0) return '—';
    const p = (n: number) => String(n).padStart(2, '0');
    return `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
  };
  const aoa: any[][] = [
    [`MONOGRAFIA DE ${m.tipo === 'ponto_apoio' ? 'PONTO DE APOIO' : 'MARCO GEODÉSICO'}`],
    [],
    ['Contratante:', m.contratante || '—'],
    [],
    ['Nome do marco:', m.codigo, '', 'Data de implantação:', m.data_implantacao ? dataSo(m.data_implantacao)!.format('DD/MM/YYYY') : '—', '', 'Município:', m.municipio || '—', 'UF:', m.uf || '—'],
    ['Marcos intervisíveis:', Array.isArray(m.marcos_intervisiveis) && m.marcos_intervisiveis.length ? m.marcos_intervisiveis.join(', ') : '—'],
    ['Datum:', m.datum || '—', '', 'Fuso:', m.fuso || '—', '', 'Meridiano Central:', m.meridiano_central || '—'],
    [],
    ['Informações do rastreio'],
    ['Início', '', 'Término', '', 'Duração:', dur(m.rastreio_inicio, m.rastreio_fim), '', 'Solução:', m.solucao || '—'],
    ['Data:', dt(m.rastreio_inicio), 'Data:', dt(m.rastreio_fim), 'Equipamento:', m.equipamento || '—', '', 'Base:', m.base || '—'],
    ['Hora:', hr(m.rastreio_inicio), 'Hora:', hr(m.rastreio_fim), 'Alt. Antena (m):', fmtN(m.altura_antena_m), '', 'Método:', m.metodo || '—'],
    [],
    ['COORDENADAS GEODÉSICAS', '', '', 'Sigma (95%):', '', 'COORDENADAS UTM'],
    ['Latitude:', m.final_lat_gms || '—', '', fmtN(m.sigma_lat_m), '', 'N (m):', fmtN(m.final_utm_n)],
    ['Longitude:', m.final_lon_gms || '—', '', fmtN(m.sigma_lon_m), '', 'E (m):', fmtN(m.final_utm_e)],
    ['Alt. Geométrica (m):', fmtN(m.final_alt_geometrica_m, 2), '', fmtN(m.sigma_alt_m), '', 'Alt. Normal (m):', fmtN(m.final_alt_normal_m, 2), 'Incerteza:', m.incerteza_m != null ? `${fmtN(m.incerteza_m, 2)} m` : '—'],
    ['Processamento:', m.processamento || '—', '', '', '', 'Fonte:', m.modelo_geoidal ? `Modelo ${m.modelo_geoidal}` : '—'],
    [],
    ['Descrição do marco:'],
    [m.descricao || '—'],
    [],
    ['Informações complementares:'],
    [m.observacoes || '—'],
    ['Responsável Técnico:', m.responsavel_tecnico || 'Lucas Rangel Martins - CREA RS214.787'],
  ];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [{ wch: 20 }, { wch: 22 }, { wch: 10 }, { wch: 18 }, { wch: 12 }, { wch: 16 }, { wch: 14 }, { wch: 14 }, { wch: 8 }, { wch: 12 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Monografia');
  XLSX.writeFile(wb, `Monografia_${m.codigo}.xlsx`);
}

// ── Form campos (compartilhado: editar + revisão da pretérita) ──────────────
// Campos numéricos QUE EXISTEM NO FORMULÁRIO. `lat`/`lon` saíram daqui em
// 01/09/2026: não há campo deles na tela (só o GMS), mas `formParaBody` fazia
// `out[c] = v[c] ?? null` — e como o antd não devolve campo sem Form.Item, ia
// `lat: null` explícito no corpo. Cada edição de marco apagava a coordenada e
// tirava o pin do mapa, calado. 22 marcos foram parar assim.
const CAMPOS_NUM = ['altura_antena_m', 'final_alt_geometrica_m', 'final_utm_n', 'final_utm_e',
  'final_alt_normal_m', 'sigma_lat_m', 'sigma_lon_m', 'sigma_alt_m', 'incerteza_m'];

function formParaBody(v: any): any {
  const out: any = { ...v };
  out.data_implantacao = v.data_implantacao ? (v.data_implantacao as Dayjs).format('YYYY-MM-DD') : null;
  out.rastreio_inicio = v.rastreio_inicio ? (v.rastreio_inicio as Dayjs).toISOString() : null;
  out.rastreio_fim = v.rastreio_fim ? (v.rastreio_fim as Dayjs).toISOString() : null;
  for (const c of CAMPOS_NUM) out[c] = v[c] ?? null;
  return out;
}
function marcoParaForm(m: any): any {
  // dayjs inválido não pode chegar no DatePicker (crasha o toISOString no salvar)
  const dj = (v: any): Dayjs | null => {
    if (!v) return null;
    const d = dayjs(v);
    return d.isValid() ? d : null;
  };
  return {
    ...m,
    data_implantacao: dataSo(m.data_implantacao),
    rastreio_inicio: dj(m.rastreio_inicio),
    rastreio_fim: dj(m.rastreio_fim),
    ...Object.fromEntries(CAMPOS_NUM.map((c) => [c, num(m[c])])),
  };
}

function FormMarco({ form, contratos }: { form: any; contratos: ContratoOpt[] }) {
  return (
    <Form form={form} layout="vertical" size="small">
      <Row gutter={8}>
        <Col span={5}><Form.Item name="codigo" label="Código" rules={[{ required: true }]}><Input /></Form.Item></Col>
        <Col span={5}><Form.Item name="tipo" label="Tipo"><Select options={[{ value: 'marco', label: 'Marco' }, { value: 'ponto_apoio', label: 'Ponto de apoio' }]} /></Form.Item></Col>
        <Col span={9}><Form.Item name="contrato_id" label="Contrato"><Select allowClear showSearch optionFilterProp="label"
          options={contratos.map((c) => ({ value: c.id, label: `${c.numero || '—'} · ${c.titulo || ''}` }))} /></Form.Item></Col>
        <Col span={5}><Form.Item name="status" label="Status"><Select options={Object.entries(STATUS_INFO).map(([v, i]) => ({ value: v, label: i.rotulo }))} /></Form.Item></Col>
      </Row>
      <Row gutter={8}>
        <Col span={6}><Form.Item name="data_implantacao" label="Data de implantação"><DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} /></Form.Item></Col>
        <Col span={8}><Form.Item name="municipio" label="Município"><Input /></Form.Item></Col>
        <Col span={3}><Form.Item name="uf" label="UF"><Input maxLength={2} /></Form.Item></Col>
        <Col span={7}><Form.Item name="marcos_intervisiveis" label="Marcos intervisíveis"><Select mode="tags" open={false} suffixIcon={null} /></Form.Item></Col>
      </Row>
      {/* forceRender: campos em painel RECOLHIDO precisam existir no form,
          senão o validateFields() do salvar descarta o que a IA preencheu
          (rastreio/antena sumiam — caso M01 do Rodrigo, 2026-07-21) */}
      <Collapse size="small" defaultActiveKey={['coords']} items={[
        {
          key: 'coords', label: 'Coordenadas finais (PPP / monografia)', forceRender: true,
          children: (
            <>
              <Row gutter={8}>
                <Col span={8}><Form.Item name="final_lat_gms" label={<>Latitude GMS <Text type="secondary">(27°13'51,9420" S)</Text></>}><Input /></Form.Item></Col>
                <Col span={8}><Form.Item name="final_lon_gms" label="Longitude GMS"><Input /></Form.Item></Col>
                <Col span={4}><Form.Item name="final_alt_geometrica_m" label="Alt. geom. (m)"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
                <Col span={4}><Form.Item name="final_alt_normal_m" label="Alt. normal (m)"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
              </Row>
              <Row gutter={8}>
                <Col span={5}><Form.Item name="final_utm_n" label="UTM N (m)"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                <Col span={5}><Form.Item name="final_utm_e" label="UTM E (m)"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                <Col span={4}><Form.Item name="sigma_lat_m" label="σ lat (m)"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                <Col span={4}><Form.Item name="sigma_lon_m" label="σ lon (m)"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                <Col span={3}><Form.Item name="sigma_alt_m" label="σ alt (m)"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
                <Col span={3}><Form.Item name="incerteza_m" label="Incert. (m)"><InputNumber style={{ width: '100%' }} step={0.01} /></Form.Item></Col>
              </Row>
              <Row gutter={8}>
                <Col span={5}><Form.Item name="datum" label="Datum"><Input /></Form.Item></Col>
                <Col span={4}><Form.Item name="fuso" label="Fuso"><Input placeholder="22S" /></Form.Item></Col>
                <Col span={4}><Form.Item name="meridiano_central" label="Merid. central"><Input placeholder="-51°" /></Form.Item></Col>
                <Col span={5}><Form.Item name="processamento" label="Processamento"><Input placeholder="PPP - IBGE" /></Form.Item></Col>
                <Col span={6}><Form.Item name="modelo_geoidal" label="Modelo geoidal"><Input placeholder="hgeoHNOR_IMBITUBA" /></Form.Item></Col>
              </Row>
            </>
          ),
        },
        {
          key: 'rastreio', label: 'Rastreio GNSS', forceRender: true,
          children: (
            <Row gutter={8}>
              <Col span={6}><Form.Item name="rastreio_inicio" label="Início"><DatePicker showTime format="DD/MM/YYYY HH:mm:ss" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={6}><Form.Item name="rastreio_fim" label="Término"><DatePicker showTime format="DD/MM/YYYY HH:mm:ss" style={{ width: '100%' }} /></Form.Item></Col>
              <Col span={4}><Form.Item name="metodo" label="Método"><Input /></Form.Item></Col>
              <Col span={4}><Form.Item name="solucao" label="Solução"><Select allowClear options={[{ value: 'Fixa' }, { value: 'Flutuante' }]} /></Form.Item></Col>
              <Col span={4}><Form.Item name="altura_antena_m" label="Alt. antena (m)"><InputNumber style={{ width: '100%' }} step={0.001} /></Form.Item></Col>
              <Col span={12}><Form.Item name="equipamento" label="Equipamento"><Input placeholder="GNSS CHCNav i73+" /></Form.Item></Col>
              <Col span={12}><Form.Item name="base" label="Base"><Input placeholder="RBMC POAL / — " /></Form.Item></Col>
            </Row>
          ),
        },
        {
          key: 'textos', label: 'Descrição, observações e RT', forceRender: true,
          children: (
            <>
              <Form.Item name="descricao" label="Descrição / localização do marco"><Input.TextArea rows={3} /></Form.Item>
              <Form.Item name="observacoes" label="Informações complementares"><Input.TextArea rows={2} /></Form.Item>
              <Form.Item name="responsavel_tecnico" label="Responsável Técnico"><Input placeholder="Lucas Rangel Martins - CREA RS214.787" /></Form.Item>
            </>
          ),
        },
      ]} />
    </Form>
  );
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function OperacionalMarcos() {
  return <AntApp><MarcosInner /></AntApp>;
}

function MarcosInner() {
  const { message } = AntApp.useApp();
  const { pode } = usePerms();
  const podeConsolidar = pode('operacional.marcos.consolidar') || pode('admin.permissoes.editar');

  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const fichaRef = useRef<(id: string) => void>(() => {});
  const [marcos, setMarcos] = useState<MarcoLista[]>([]);
  const [contratos, setContratos] = useState<ContratoOpt[]>([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [fContrato, setFContrato] = useState<string | undefined>();
  const [fStatus, setFStatus] = useState<string | undefined>();
  const [estilo, setEstilo] = useState<'osm' | 'satelite'>('osm');
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [preteritaOpen, setPreteritaOpen] = useState(false);

  async function carregar() {
    setLoading(true);
    try {
      const d = await mob<{ marcos: MarcoLista[] }>('get', '/mobile/marcos');
      setMarcos(d.marcos || []);
    } catch (e: any) {
      message.error('Erro ao carregar marcos: ' + (e.response?.data?.error || e.message));
    } finally { setLoading(false); }
  }
  useEffect(() => {
    carregar();
    listarContratosOpts().then(setContratos).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  fichaRef.current = (id) => setFichaId(id);

  const filtrados = useMemo(() => marcos.filter((m) => {
    if (fContrato && m.contrato_id !== fContrato) return false;
    if (fStatus && m.status !== fStatus) return false;
    if (busca.trim()) {
      const q = busca.trim().toLowerCase();
      if (![m.codigo, m.municipio, m.contrato_numero, m.criado_por].some((v) => (v || '').toLowerCase().includes(q))) return false;
    }
    return true;
  }), [marcos, fContrato, fStatus, busca]);

  // ── mapa ──
  function estiloMapa(qual: 'osm' | 'satelite'): any {
    return qual === 'osm'
      ? { version: 8, sources: { base: { type: 'raster', tiles: ['https://a.tile.openstreetmap.org/{z}/{x}/{y}.png', 'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '© OpenStreetMap' } }, layers: [{ id: 'base', type: 'raster', source: 'base' }] }
      : { version: 8, sources: { base: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, attribution: 'Esri' } }, layers: [{ id: 'base', type: 'raster', source: 'base' }] };
  }
  function addCamadaMarcos(map: MapLibreMap) {
    if (map.getSource('marcos')) return;
    map.addSource('marcos', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } as any });
    map.addLayer({
      id: 'marcos-pt', source: 'marcos', type: 'circle',
      paint: {
        'circle-radius': 8,
        'circle-color': ['match', ['get', 'status'], 'campo', STATUS_COR_MAPA.campo, 'processado', STATUS_COR_MAPA.processado, STATUS_COR_MAPA.validado],
        'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
      },
    });
    map.addLayer({
      id: 'marcos-lbl', source: 'marcos', type: 'symbol',
      layout: { 'text-field': ['get', 'codigo'], 'text-size': 11, 'text-offset': [0, 1.4], 'text-anchor': 'top' },
      paint: { 'text-color': '#222', 'text-halo-color': '#fff', 'text-halo-width': 1.5 },
    });
    map.on('click', 'marcos-pt', (e) => { const id = e.features?.[0]?.properties?.id; if (id) fichaRef.current(String(id)); });
    map.on('mouseenter', 'marcos-pt', () => { map.getCanvas().style.cursor = 'pointer'; });
    map.on('mouseleave', 'marcos-pt', () => { map.getCanvas().style.cursor = ''; });
  }
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = new maplibregl.Map({ container: containerRef.current, style: estiloMapa('osm'), center: [-52.0, -28.5], zoom: 5.5 });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: false }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    mapRef.current = map;
    map.on('load', () => addCamadaMarcos(map));
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.setStyle(estiloMapa(estilo));
    map.once('idle', () => { addCamadaMarcos(map); aplicarPontos(map, filtrados, false); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estilo]);
  function aplicarPontos(map: MapLibreMap, lista: MarcoLista[], fit: boolean) {
    const src = map.getSource('marcos') as any;
    if (!src) return;
    const comGeo = lista.filter((m) => m.lat != null && m.lon != null);
    src.setData({
      type: 'FeatureCollection',
      features: comGeo.map((m) => ({
        type: 'Feature', geometry: { type: 'Point', coordinates: [Number(m.lon), Number(m.lat)] },
        properties: { id: m.id, codigo: m.codigo, status: m.status },
      })),
    });
    if (fit && comGeo.length) {
      let w = Infinity, s = Infinity, e = -Infinity, n = -Infinity;
      for (const m of comGeo) { w = Math.min(w, Number(m.lon)); s = Math.min(s, Number(m.lat)); e = Math.max(e, Number(m.lon)); n = Math.max(n, Number(m.lat)); }
      try { map.fitBounds([[w, s], [e, n]], { padding: 60, maxZoom: 14, duration: 500 }); } catch { /* bounds degenerado */ }
    }
  }
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => aplicarPontos(map, filtrados, true);
    if (map.isStyleLoaded() && map.getSource('marcos')) apply();
    else map.once('idle', apply);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrados]);

  const contagens = useMemo(() => {
    const c: Record<string, number> = { campo: 0, processado: 0, validado: 0 };
    for (const m of marcos) c[m.status] = (c[m.status] || 0) + 1;
    return c;
  }, [marcos]);

  const colunas: any[] = [
    // 🚨 O código NÃO cabe em 90px desde a importação de monografias: elas
    // trazem o código com o lugar junto ("M15 - SANTA TEREZA"), não o "M6500"
    // que o sistema sugere. Sem `ellipsis`, o texto vazava POR CIMA da coluna
    // Tipo — o "Marco" ficava escrito em cima do nome. Largura maior + corte
    // com reticências + o nome inteiro no tooltip (e o botão precisa do
    // overflow próprio: `ellipsis` corta a CÉLULA, não o filho que transborda).
    { title: 'Código', dataIndex: 'codigo', width: 210, ellipsis: { showTitle: false },
      render: (v: string, m: MarcoLista) => (
        <Tooltip title={v} placement="topLeft">
          <Button type="link" size="small" onClick={() => setFichaId(m.id)}
            style={{ padding: 0, maxWidth: '100%', overflow: 'hidden',
                     textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {v}
          </Button>
        </Tooltip>
      ) },
    { title: 'Tipo', dataIndex: 'tipo', width: 110, render: (v: string) => (v === 'ponto_apoio' ? 'Ponto de apoio' : 'Marco') },
    { title: 'Contrato', dataIndex: 'contrato_numero', width: 110, ellipsis: true,
      render: (v: string) => v || <Text type="secondary">—</Text> },
    { title: 'Município/UF', key: 'mun', ellipsis: true, render: (_: any, m: MarcoLista) => [m.municipio, m.uf].filter(Boolean).join('/') || <Text type="secondary">—</Text> },
    { title: 'Implantação', dataIndex: 'data_implantacao', width: 105, render: (v: string) => (v ? dataSo(v)!.format('DD/MM/YYYY') : '—') },
    { title: 'Alt. normal (m)', dataIndex: 'final_alt_normal_m', width: 110, render: (v: any) => fmtN(v, 2) },
    { title: 'Status', dataIndex: 'status', width: 105, render: (v: string) => <Tag color={STATUS_INFO[v]?.cor}>{STATUS_INFO[v]?.rotulo || v}</Tag> },
    { title: 'Origem', dataIndex: 'fonte', width: 95, render: (v: string) => ({ app: 'PWA campo', web: 'Web', importado: 'Importado' } as any)[v] || v || '—' },
  ];

  return (
    <div style={{ padding: 16 }}>
      <Row align="middle" justify="space-between" style={{ marginBottom: 12 }} gutter={[8, 8]}>
        <Col>
          <Space align="center">
            <Title level={4} style={{ margin: 0 }}><EnvironmentOutlined /> Marcos Geodésicos</Title>
            <Tag color="orange">{contagens.campo} em campo</Tag>
            <Tag color="blue">{contagens.processado} processados</Tag>
            <Tag color="green">{contagens.validado} validados</Tag>
          </Space>
        </Col>
        <Col>
          <Space wrap>
            <Input.Search allowClear placeholder="Buscar código, município, contrato…" style={{ width: 240 }} onSearch={setBusca} onChange={(e) => !e.target.value && setBusca('')} />
            <Select allowClear showSearch optionFilterProp="label" placeholder="Contrato" style={{ minWidth: 170 }}
              value={fContrato} onChange={setFContrato}
              options={contratos.map((c) => ({ value: c.id, label: c.numero || c.titulo || c.id }))} />
            <Select allowClear placeholder="Status" style={{ width: 130 }} value={fStatus} onChange={setFStatus}
              options={Object.entries(STATUS_INFO).map(([v, i]) => ({ value: v, label: i.rotulo }))} />
            <Segmented size="small" value={estilo} onChange={(v) => setEstilo(v as any)}
              options={[{ value: 'osm', label: 'Mapa' }, { value: 'satelite', label: 'Satélite' }]} />
            <Button icon={<ReloadOutlined />} onClick={carregar} />
            {podeConsolidar && (
              <Button type="primary" icon={<CloudUploadOutlined />} onClick={() => setPreteritaOpen(true)}>
                Importar monografia
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <div style={{ height: 420, borderRadius: 8, overflow: 'hidden', border: '1px solid #eee', marginBottom: 16, position: 'relative' }}>
        <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />
      </div>

      <Table rowKey="id" size="small" loading={loading} columns={colunas} dataSource={filtrados}
        locale={{ emptyText: <Empty description="Nenhum marco encontrado" /> }}
        pagination={{ pageSize: 25, hideOnSinglePage: true, showTotal: (t) => `${t} marcos` }} />

      {fichaId && (
        <FichaMarco id={fichaId} contratos={contratos} podeConsolidar={podeConsolidar}
          onClose={() => setFichaId(null)} onMudou={carregar} />
      )}
      {/* onCriado só RECARREGA a lista. Fechar é só pelo onClose: o modal salva
          N marcos em sequência, e fechá-lo no primeiro jogava a fila inteira
          fora (arquivos já enviados e leituras de IA já pagas). */}
      {preteritaOpen && (
        <ModalPreterita contratos={contratos} onClose={() => setPreteritaOpen(false)}
          onCriado={carregar} />
      )}
    </div>
  );
}

// ── Ficha do marco (Drawer) ─────────────────────────────────────────────────
function FichaMarco({ id, contratos, podeConsolidar, onClose, onMudou }: {
  id: string; contratos: ContratoOpt[]; podeConsolidar: boolean;
  onClose: () => void; onMudou: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [dados, setDados] = useState<{ marco: any; fotos: FotoMarco[]; anexos: Anexo[] } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [pppLoading, setPppLoading] = useState(false);
  const [form] = Form.useForm();

  async function carregar() {
    try {
      const d = await mob('get', `/mobile/marcos/${id}`);
      setDados({ marco: d.marco, fotos: d.fotos || [], anexos: d.anexos || [] });
    } catch (e: any) { message.error('Erro ao abrir o marco: ' + (e.response?.data?.error || e.message)); }
  }
  useEffect(() => { carregar(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const m = dados?.marco;

  async function salvarEdicao() {
    const v = await form.validateFields();
    setSalvando(true);
    try {
      await mob('patch', `/mobile/marcos/${id}/web`, formParaBody(v));
      message.success('Marco atualizado.');
      setEditOpen(false); carregar(); onMudou();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
    finally { setSalvando(false); }
  }

  async function validar() {
    try {
      await mob('patch', `/mobile/marcos/${id}/web`, { status: 'validado' });
      message.success('Marco validado.'); carregar(); onMudou();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
  }

  async function excluir() {
    try {
      await mob('delete', `/mobile/marcos/${id}`);
      message.success('Marco excluído.'); onClose(); onMudou();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
  }

  async function gerarPdf() {
    setGerando(true);
    try {
      const r = await mob('post', `/mobile/marcos/${id}/monografia`);
      message.success('Monografia gerada.');
      if (r.url) window.open(r.url, '_blank');
      carregar();
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
    finally { setGerando(false); }
  }

  async function consolidarPpp(file: File) {
    setPppLoading(true);
    try {
      const up = await mob('post', `/mobile/marcos/${id}/anexos`, { nome: file.name, tamanho_bytes: file.size, tipo: 'ppp_pdf' });
      await axios.put(up.upload_url, file, { headers: { 'Content-Type': 'application/pdf' } });
      const r = await mob('post', `/mobile/marcos/${id}/consolidar-ppp`, { s3_key: up.s3_key });
      const c = r.campos || {};
      modal.confirm({
        width: 640,
        title: 'Consolidar com o PPP-IBGE',
        icon: <AimOutlined />,
        content: (
          <div>
            {r.diff?.distancia_m != null && (
              <p>Distância entre a coordenada preliminar (GPS de campo) e a do PPP: <b>{r.diff.distancia_m} m</b>.</p>
            )}
            <Descriptions size="small" column={2} bordered items={[
              { key: '1', label: 'Latitude', children: c.final_lat_gms || '—' },
              { key: '2', label: 'Longitude', children: c.final_lon_gms || '—' },
              { key: '3', label: 'Alt. geométrica', children: fmtN(c.final_alt_geometrica_m, 2) },
              { key: '4', label: 'Alt. normal', children: fmtN(c.final_alt_normal_m, 2) },
              { key: '5', label: 'UTM N', children: fmtN(c.final_utm_n) },
              { key: '6', label: 'UTM E', children: fmtN(c.final_utm_e) },
              { key: '7', label: 'σ lat / lon / alt', children: `${fmtN(c.sigma_lat_m)} / ${fmtN(c.sigma_lon_m)} / ${fmtN(c.sigma_alt_m)}` },
              { key: '8', label: 'Modelo geoidal', children: c.modelo_geoidal || '—' },
            ]} />
            <p style={{ marginTop: 8 }}>Aplicar essas coordenadas como <b>finais</b> (status → Processado)? Você pode ajustar depois em Editar.</p>
          </div>
        ),
        okText: 'Aplicar',
        onOk: async () => {
          await mob('patch', `/mobile/marcos/${id}/web`, {
            final_lat_gms: c.final_lat_gms, final_lon_gms: c.final_lon_gms,
            final_alt_geometrica_m: c.final_alt_geometrica_m ?? null,
            final_utm_n: c.final_utm_n ?? null, final_utm_e: c.final_utm_e ?? null,
            final_alt_normal_m: c.final_alt_normal_m ?? null,
            sigma_lat_m: c.sigma_lat_m ?? null, sigma_lon_m: c.sigma_lon_m ?? null,
            sigma_alt_m: c.sigma_alt_m ?? null,
            fuso: c.fuso ?? undefined, meridiano_central: c.meridiano_central ?? undefined,
            modelo_geoidal: c.modelo_geoidal ?? undefined, solucao: c.solucao ?? undefined,
            processamento: c.processamento || 'PPP - IBGE',
            lat: c.lat ?? undefined, lon: c.lon ?? undefined,
            status: 'processado',
          });
          message.success('Coordenadas do PPP aplicadas.');
          carregar(); onMudou();
        },
      });
    } catch (e: any) { message.error(e.response?.data?.error || e.message); }
    finally { setPppLoading(false); }
  }

  const tipoAnexo = (t: string) => ({ monografia_pdf: 'Monografia (PDF)', monografia_xlsx: 'Monografia (XLSX)', ppp_pdf: 'Relatório PPP', outro: 'Anexo' } as any)[t] || t;

  return (
    <Drawer open width={860} onClose={onClose}
      title={m ? <Space>{m.codigo} <Tag color={STATUS_INFO[m.status]?.cor}>{STATUS_INFO[m.status]?.rotulo}</Tag>{m.contrato_numero && <Tag>{m.contrato_numero}</Tag>}</Space> : 'Carregando…'}
      extra={m && podeConsolidar && (
        <Space wrap>
          <Upload accept=".pdf" showUploadList={false} beforeUpload={(f) => { consolidarPpp(f as any); return false; }}>
            <Button size="small" icon={<AimOutlined />} loading={pppLoading}>Consolidar PPP</Button>
          </Upload>
          {m.status !== 'validado' && (
            <Button size="small" icon={<SafetyCertificateOutlined />} onClick={validar}>Validar</Button>
          )}
          <Button size="small" icon={<FilePdfOutlined />} loading={gerando} onClick={gerarPdf}>Monografia PDF</Button>
          <Button size="small" icon={<FileExcelOutlined />} onClick={() => baixarMonografiaXlsx(m)}>XLSX</Button>
          <Button size="small" icon={<EditOutlined />} onClick={() => { form.setFieldsValue(marcoParaForm(m)); setEditOpen(true); }}>Editar</Button>
          <Popconfirm title="Excluir este marco? Fotos e anexos saem do acervo." okText="Excluir" okButtonProps={{ danger: true }} onConfirm={excluir}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )}>
      {!dados ? <Spin /> : (
        <>
          <Descriptions size="small" column={2} bordered style={{ marginBottom: 12 }} items={[
            { key: 't', label: 'Tipo', children: m.tipo === 'ponto_apoio' ? 'Ponto de apoio' : 'Marco' },
            { key: 'd', label: 'Implantação', children: m.data_implantacao ? dataSo(m.data_implantacao)!.format('DD/MM/YYYY') : '—' },
            { key: 'mu', label: 'Município/UF', children: [m.municipio, m.uf].filter(Boolean).join('/') || '—' },
            { key: 'eq', label: 'Equipamento', children: m.equipamento || '—' },
            { key: 'ra', label: 'Rastreio', children: m.rastreio_inicio ? `${dayjs(m.rastreio_inicio).format('DD/MM HH:mm')} → ${m.rastreio_fim ? dayjs(m.rastreio_fim).format('DD/MM HH:mm') : '?'}` : '—' },
            { key: 'au', label: 'Cadastrado por', children: `${m.criado_por || '—'} (${({ app: 'PWA', web: 'web', importado: 'importado' } as any)[m.fonte] || m.fonte})` },
          ]} />

          <Row gutter={12}>
            <Col span={12}>
              <Descriptions size="small" column={1} bordered title="Preliminar (GPS de campo)" items={[
                { key: '1', label: 'Lat / Lon', children: (m.fonte === 'app' && m.accuracy_m != null) ? `${fmtN(m.lat, 7)} / ${fmtN(m.lon, 7)} (±${fmtN(m.accuracy_m, 1)} m)` : `${fmtN(m.lat, 7)} / ${fmtN(m.lon, 7)}` },
                { key: '2', label: 'Altitude GPS (m)', children: fmtN(m.altitude_m, 1) },
              ]} />
            </Col>
            <Col span={12}>
              <Descriptions size="small" column={1} bordered title="Finais (consolidadas)" items={[
                { key: '1', label: 'Latitude', children: m.final_lat_gms || '—' },
                { key: '2', label: 'Longitude', children: m.final_lon_gms || '—' },
                { key: '3', label: 'Alt. geom. / normal (m)', children: `${fmtN(m.final_alt_geometrica_m, 2)} / ${fmtN(m.final_alt_normal_m, 2)}` },
                { key: '4', label: 'UTM N / E (m)', children: `${fmtN(m.final_utm_n)} / ${fmtN(m.final_utm_e)}` },
                { key: '5', label: 'σ (lat/lon/alt) m', children: `${fmtN(m.sigma_lat_m)} / ${fmtN(m.sigma_lon_m)} / ${fmtN(m.sigma_alt_m)}` },
                { key: '6', label: 'Incerteza (m)', children: fmtN(m.incerteza_m, 2) },
                { key: '7', label: 'Processamento', children: `${m.processamento || '—'}${m.modelo_geoidal ? ` · ${m.modelo_geoidal}` : ''}` },
              ]} />
            </Col>
          </Row>

          {(m.descricao || m.observacoes) && (
            <Descriptions size="small" column={1} bordered style={{ marginTop: 12 }} items={[
              ...(m.descricao ? [{ key: 'de', label: 'Descrição', children: <span style={{ whiteSpace: 'pre-wrap' }}>{m.descricao}</span> }] : []),
              ...(m.observacoes ? [{ key: 'ob', label: 'Observações', children: <span style={{ whiteSpace: 'pre-wrap' }}>{m.observacoes}</span> }] : []),
            ]} />
          )}

          {dados.anexos.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>Anexos</Title>
              <Space direction="vertical" style={{ width: '100%' }} size={4}>
                {dados.anexos.map((a, i) => (
                  <Space key={`${a.s3_key}-${i}`} style={{ width: '100%', justifyContent: 'space-between', border: '1px solid #f0f0f0', borderRadius: 6, padding: '4px 10px' }}>
                    <Space>
                      {a.tipo?.includes('xlsx') ? <FileExcelOutlined /> : <FilePdfOutlined />}
                      <span>{a.nome || a.s3_key.split('/').pop()}</span>
                      <Tag>{tipoAnexo(a.tipo)}</Tag>
                      {a.origem === 'gerado' && <Tag color="purple">gerado</Tag>}
                    </Space>
                    <Space size={4}>
                      {a.url && <Button size="small" type="link" icon={<DownloadOutlined />} href={a.url} target="_blank">Baixar</Button>}
                      {podeConsolidar && (
                        <Popconfirm title="Remover anexo do marco?" onConfirm={async () => {
                          await mob('delete', `/mobile/marcos/${id}/anexos`, { s3_key: a.s3_key });
                          carregar();
                        }}>
                          <Button size="small" type="text" danger icon={<DeleteOutlined />} />
                        </Popconfirm>
                      )}
                    </Space>
                  </Space>
                ))}
              </Space>
            </>
          )}

          {dados.fotos.length > 0 && (
            <>
              <Title level={5} style={{ marginTop: 16 }}>Fotos ({dados.fotos.length})</Title>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {dados.fotos.map((f) => (
                  <a key={f.id} href={f.url} target="_blank" rel="noreferrer">
                    <img src={f.url} alt={f.descricao || ''} style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 6, border: '1px solid #eee' }} />
                  </a>
                ))}
              </div>
            </>
          )}

          {Array.isArray(m.historico) && m.historico.length > 0 && (
            <Collapse size="small" style={{ marginTop: 16 }} items={[{
              key: 'h', label: `Histórico (${m.historico.length})`,
              children: (
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  {[...m.historico].reverse().map((h: any, i: number) => (
                    <Text key={i} type="secondary" style={{ fontSize: 12 }}>
                      {dayjs(h.quando).format('DD/MM/YYYY HH:mm')} · {h.quem} · {(h.alteracoes || []).map((a: any) => a.campo).join(', ')}
                    </Text>
                  ))}
                </Space>
              ),
            }]} />
          )}

          <Modal open={editOpen} width={900} title={`Editar ${m.codigo}`} okText="Salvar"
            confirmLoading={salvando} onOk={salvarEdicao} onCancel={() => setEditOpen(false)} destroyOnClose>
            <FormMarco form={form} contratos={contratos} />
          </Modal>
        </>
      )}
    </Drawer>
  );
}

// ── Importar monografias em LOTE ────────────────────────────────────────────
// Pedido do Lucas (01/09/2026): "upload de monografias em batch, similar ao
// envio dos contratos".
//
// 🚨 O que estava errado antes: o Dragger já aceitava `multiple`, mas os N
// arquivos caíam TODOS no mesmo formulário — cada extração sobrescrevia a
// anterior, e o resultado eram N anexos pendurados em UM marco só, com os
// dados do último arquivo. Quem soltava 20 monografias perdia 19.
//
// Agora é fila, no mesmo desenho do ModalIncluirAssinado do Jurídico: cada
// arquivo é um marco; enquanto você confere o primeiro, os outros já estão
// sendo lidos; e nada vira marco antes de alguém clicar em salvar — leitura de
// PDF erra coordenada, e coordenada errada é marco errado no campo.
type EtapaLote = 'espera' | 'subindo' | 'lendo' | 'pronto' | 'erro' | 'salvo' | 'adiado';

interface ItemLote {
  uid: string;
  file: File;
  nome: string;
  etapa: EtapaLote;
  anexo?: Anexo;
  campos?: Record<string, any>;
  erro?: string;
  /** marco que já existia com o mesmo código (409) — o anexo vai pra ele. */
  duplicado?: { id: string; codigo: string; motivo?: string; distancia_m?: number };
  /** o que a pessoa já digitou neste item (sobrevive a pular e voltar). */
  rascunho?: Record<string, any>;
}

const MAX_MONOGRAFIAS = 30;
// Quantas leituras da IA ao mesmo tempo. Cada uma é uma chamada HTTP própria
// (o extrair-monografia é síncrono); 3 mantém a fila andando sem enfileirar
// Lambda à toa.
const LEITURAS_SIMULTANEAS = 3;

const ETAPA_LOTE_CFG: Record<EtapaLote, { rotulo: string; cor?: string }> = {
  espera: { rotulo: 'na fila' },
  subindo: { rotulo: 'enviando', cor: 'blue' },
  lendo: { rotulo: 'lendo', cor: 'blue' },
  pronto: { rotulo: 'confira', cor: 'gold' },
  erro: { rotulo: 'falhou', cor: 'red' },
  salvo: { rotulo: 'salvo', cor: 'green' },
  adiado: { rotulo: 'adiado' },
};

function ModalPreterita({ contratos, onClose, onCriado }: {
  contratos: ContratoOpt[]; onClose: () => void; onCriado: () => void;
}) {
  const { message, modal } = AntApp.useApp();
  const [form] = Form.useForm();
  const [itens, setItens] = useState<ItemLote[]>([]);
  const [atualUid, setAtualUid] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const itensRef = useRef<ItemLote[]>([]);
  itensRef.current = itens;
  // 🚨 `beforeUpload` do antd roda UMA VEZ POR ARQUIVO, cada uma recebendo a
  // leva inteira. Sem esta trava, soltar 10 monografias chamaria o loop 10
  // vezes: 100 uploads e 100 leituras da IA (que se paga por chamada). O antd
  // passa a MESMA instância do array em todas as chamadas da leva — é isso que
  // a comparação por referência aproveita.
  const loteRef = useRef<File[] | null>(null);

  const atual = itens.find((i) => i.uid === atualUid) || null;
  const salvos = itens.filter((i) => i.etapa === 'salvo').length;
  const trabalhando = itens.some((i) => i.etapa === 'subindo' || i.etapa === 'lendo' || i.etapa === 'espera');

  function alterar(uid: string, patch: Partial<ItemLote>) {
    setItens((antes) => antes.map((i) => (i.uid === uid ? { ...i, ...patch } : i)));
  }

  // ── Subir e ler ──────────────────────────────────────────────────────────
  // Upload serial (um PUT grande de cada vez economiza a rede do escritório de
  // campo); a leitura de cada arquivo dispara assim que ele chega, até
  // LEITURAS_SIMULTANEAS ao mesmo tempo.
  async function receberArquivos(files: File[]) {
    const aceitos: File[] = [];
    for (const f of files) {
      const ext = (f.name.split('.').pop() || '').toLowerCase();
      if (['pdf', 'xlsx', 'xls'].includes(ext)) aceitos.push(f);
    }
    if (aceitos.length < files.length) {
      message.warning('Só entram PDF e XLSX — os demais arquivos foram ignorados.');
    }
    if (!aceitos.length) return;
    // Segunda trava, por conteúdo: se a comparação por referência falhar num
    // antd futuro, o mesmo arquivo ainda não entra duas vezes na fila.
    const jaNaFila = new Set(itensRef.current.map((i) => `${i.nome}|${i.file.size}|${i.file.lastModified}`));
    const inditos = aceitos.filter((f) => !jaNaFila.has(`${f.name}|${f.size}|${f.lastModified}`));
    if (!inditos.length) return;
    if (inditos.length > MAX_MONOGRAFIAS) {
      message.warning(`Peguei as primeiras ${MAX_MONOGRAFIAS}. Depois é só repetir com o resto.`);
    }
    const novos: ItemLote[] = inditos.slice(0, MAX_MONOGRAFIAS).map((f, n) => ({
      uid: `${Date.now()}-${n}-${f.name}`, file: f, nome: f.name, etapa: 'espera' as EtapaLote,
    }));
    setItens((antes) => [...antes, ...novos]);

    // fila de leitura com concorrência limitada
    let emVoo = 0;
    const pendentes: (() => Promise<void>)[] = [];
    const puxar = () => {
      while (emVoo < LEITURAS_SIMULTANEAS && pendentes.length) {
        const tarefa = pendentes.shift()!;
        emVoo++;
        tarefa().finally(() => { emVoo--; puxar(); });
      }
    };

    for (const it of novos) {
      const ext = (it.nome.split('.').pop() || '').toLowerCase();
      try {
        alterar(it.uid, { etapa: 'subindo' });
        const up = await mob('post', '/mobile/marcos/anexos-upload-url', {
          nome: it.file.name, tamanho_bytes: it.file.size,
          tipo: ext === 'pdf' ? 'monografia_pdf' : 'monografia_xlsx',
        });
        await axios.put(up.upload_url, it.file, { headers: { 'Content-Type': up.anexo.content_type } });
        alterar(it.uid, { anexo: up.anexo, etapa: 'lendo' });

        if (ext !== 'pdf') {
          // XLSX no modelo Hydrodata: lê aqui mesmo, sem IA e sem espera.
          const campos = parseMonografiaXlsx(await it.file.arrayBuffer());
          alterar(it.uid, { etapa: 'pronto', campos });
        } else {
          pendentes.push(async () => {
            try {
              const r = await mob('post', '/mobile/marcos/extrair-monografia', { s3_key: up.s3_key });
              alterar(it.uid, { etapa: 'pronto', campos: r.campos || {} });
            } catch (e: any) {
              alterar(it.uid, { etapa: 'erro', erro: e.response?.data?.error || e.message });
            }
          });
          puxar();
        }
      } catch (e: any) {
        alterar(it.uid, { etapa: 'erro', erro: e.response?.data?.error || e.message });
      }
    }
  }

  // Assim que houver um pronto e nenhum aberto, abre o próximo pra conferência.
  useEffect(() => {
    if (atualUid) return;
    const proximo = itens.find((i) => i.etapa === 'pronto');
    if (proximo) setAtualUid(proximo.uid);
  }, [itens, atualUid]);

  // Preenche o formulário do arquivo ATUAL.
  //
  // 🚨 A dependência é o `uid`, NÃO o objeto `atual`. `alterar()` recria o
  // item a cada patch (marcar duplicado, mudar etapa), e com `[atual]` o
  // efeito rodava de novo e reescrevia o formulário POR CIMA do que a pessoa
  // tinha acabado de digitar — a correção da coordenada sumia em silêncio, e
  // coordenada errada é marco errado no campo.
  //
  // E o que ela digitou fica guardado no item (`rascunho`): pular e voltar,
  // ou tomar um 409, não pode custar o trabalho de conferência.
  useEffect(() => {
    if (!atualUid) return;
    const it = itensRef.current.find((i) => i.uid === atualUid);
    if (!it) return;
    form.resetFields();
    if (it.rascunho) { form.setFieldsValue(it.rascunho); return; }
    const c = it.campos || {};
    const soPreenchidos = Object.fromEntries(
      Object.entries(c).filter(([, v]) => v != null && v !== ''));
    form.setFieldsValue(marcoParaForm({ ...soPreenchidos }));
  }, [atualUid, form]);

  /** Guarda o que está no formulário dentro do item, antes de trocar de item. */
  function guardarRascunho(uid: string) {
    try { alterar(uid, { rascunho: form.getFieldsValue() }); } catch { /* form já desmontou */ }
  }

  function proximoDepoisDe(uid: string) {
    const resto = itensRef.current.filter((i) => i.uid !== uid && i.etapa === 'pronto');
    setAtualUid(resto[0]?.uid || null);
  }

  async function salvar(confirmarProximo = false) {
    if (!atual) return;
    let v: any;
    try { v = await form.validateFields(); } catch { return; }
    setSalvando(true);
    try {
      await mob('post', '/mobile/marcos/web', {
        ...formParaBody(v),
        fonte: 'web',
        status: v.status || 'validado',
        // "já conferi, é outro marco mesmo" — só quando a pessoa disser.
        ...(confirmarProximo ? { confirmar_proximo: true } : {}),
        // 🚨 SÓ o anexo DESTE arquivo. Mandar a lista inteira era o defeito
        // antigo: as 20 monografias viravam anexo do mesmo marco.
        anexos: atual.anexo ? [atual.anexo] : [],
      });
      // O anexo é o ponto do fluxo: sem ele o marco existe mas a monografia
      // não está no acervo. Dizer "importado" nesse caso seria mentira — e a
      // pessoa só descobriria abrindo a ficha.
      message.success(atual.anexo
        ? `${v.codigo}: marco importado com a monografia.`
        : `${v.codigo}: marco criado SEM a monografia (o arquivo não subiu).`);
      alterar(atual.uid, { etapa: 'salvo', duplicado: undefined });
      proximoDepoisDe(atual.uid);
      onCriado();
    } catch (e: any) {
      const st = e.response?.status;
      const d = e.response?.data?.body
        ? (() => { try { return JSON.parse(e.response.data.body); } catch { return e.response.data; } })()
        : e.response?.data;
      // Já existe marco com esse código no contrato: em lote isso é comum (a
      // pasta tem monografia de marco que já está no acervo). Em vez de beco
      // sem saída, oferece pendurar o arquivo no marco que existe.
      if (st === 409 && d?.marco_id) {
        alterar(atual.uid, { duplicado: {
          id: d.marco_id, codigo: d.codigo || v.codigo,
          motivo: d.motivo, distancia_m: d.distancia_m,
        } });
      } else {
        message.error(`${atual.nome}: ` + (d?.error || e.message));
      }
    } finally { setSalvando(false); }
  }

  async function anexarNoExistente() {
    if (!atual?.duplicado || !atual.anexo) return;
    setSalvando(true);
    try {
      await mob('post', `/mobile/marcos/${atual.duplicado.id}/anexos-existente`, {
        anexo: atual.anexo,
      });
      message.success(`${atual.duplicado.codigo}: monografia anexada ao marco que já existia.`);
      alterar(atual.uid, { etapa: 'salvo', duplicado: undefined });
      proximoDepoisDe(atual.uid);
      onCriado();
    } catch (e: any) {
      message.error(e.response?.data?.error || e.message);
    } finally { setSalvando(false); }
  }

  function adiar() {
    if (!atual) return;
    alterar(atual.uid, { etapa: 'adiado', rascunho: form.getFieldsValue() });
    proximoDepoisDe(atual.uid);
  }

  function fechar() {
    const pendentes = itens.filter((i) => ['pronto', 'adiado', 'lendo', 'subindo', 'espera'].includes(i.etapa));
    if (pendentes.length) {
      modal.confirm({
        title: `Fechar com ${pendentes.length} monografia(s) sem salvar?`,
        content: 'Os arquivos já enviados ficam guardados, mas nenhum marco é criado. Você teria que subir de novo.',
        okText: 'Fechar mesmo assim', cancelText: 'Continuar',
        onOk: onClose,
      });
      return;
    }
    onClose();
  }

  return (
    <Modal open width="94vw" style={{ maxWidth: 1100, top: 16 }}
      styles={{ body: { maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' } }}
      title="Importar monografias pretéritas" footer={null} onCancel={fechar} destroyOnClose>
      <Space direction="vertical" style={{ width: '100%' }} size={12}>
        {!itens.length && (
          <Upload.Dragger multiple accept=".pdf,.xlsx,.xls" showUploadList={false}
            beforeUpload={(_f, lista) => {
              if (loteRef.current === (lista as any)) return false;   // mesma leva
              loteRef.current = lista as any;
              receberArquivos(lista as any);
              return false;
            }}>
            <p className="ant-upload-drag-icon"><CloudUploadOutlined /></p>
            <p className="ant-upload-text">Arraste as monografias (vários arquivos de uma vez)</p>
            <p className="ant-upload-hint">
              Cada arquivo vira um marco. XLSX no modelo Hydrodata é lido na hora;
              PDF passa pela IA. Enquanto você confere o primeiro, os outros já estão sendo lidos.
            </p>
          </Upload.Dragger>
        )}

        {/* Fila */}
        {itens.length > 0 && (
          <Space wrap size={4}>
            {itens.map((i) => {
              const cfg = ETAPA_LOTE_CFG[i.etapa];
              return (
                <Tag key={i.uid} color={i.uid === atualUid ? 'processing' : cfg.cor}
                  icon={i.nome.toLowerCase().endsWith('.pdf') ? <FilePdfOutlined /> : <FileExcelOutlined />}
                  style={{ cursor: i.etapa === 'pronto' || i.etapa === 'adiado' ? 'pointer' : 'default' }}
                  onClick={() => {
                    if (i.etapa !== 'pronto' && i.etapa !== 'adiado') return;
                    if (atualUid && atualUid !== i.uid) guardarRascunho(atualUid);
                    alterar(i.uid, { etapa: 'pronto' });
                    setAtualUid(i.uid);
                  }}>
                  {i.nome} · {cfg.rotulo}
                </Tag>
              );
            })}
          </Space>
        )}

        {/* Erros, sem esconder */}
        {itens.filter((i) => i.etapa === 'erro').map((i) => (
          <Alert key={i.uid} type="error" showIcon
            message={`${i.nome} não pôde ser lido`}
            description={<>
              {i.erro}
              <div style={{ marginTop: 4 }}>
                <Button size="small" onClick={() => {
                  if (atualUid && atualUid !== i.uid) guardarRascunho(atualUid);
                  alterar(i.uid, { etapa: 'pronto', campos: i.campos || {} });
                  setAtualUid(i.uid);
                }}>
                  Preencher à mão{i.anexo ? '' : ' (sem o arquivo)'}
                </Button>
              </div>
            </>} />
        ))}

        {/* Dois motivos de conflito, e a saída de cada um é diferente:
            • pelo CÓDIGO — o nome já existe no contrato; dá para renomear.
            • pela COORDENADA — é o mesmo ponto com outro nome (o caso que
              deixou 21 pares duplicados entrarem). Renomear não resolve nada
              aqui; ou é o mesmo marco, ou a pessoa confirma que são dois. */}
        {atual?.duplicado && (
          <Alert type="warning" showIcon
            message={atual.duplicado.motivo === 'coordenada'
              ? `Já existe o marco ${atual.duplicado.codigo} ${
                  (atual.duplicado.distancia_m ?? 0) < 1
                    ? 'no MESMO ponto' : `a ${atual.duplicado.distancia_m} m daqui`}`
              : `Já existe o marco ${atual.duplicado.codigo} neste contrato`}
            description={<>
              {atual.duplicado.motivo === 'coordenada'
                ? 'Código de marco se repete entre projetos — quem identifica é a coordenada. '
                  + 'Quase sempre é o mesmo marco cadastrado com outro nome.'
                : 'Isso é comum quando a pasta tem monografia de marco que já está no acervo.'}
              <div style={{ marginTop: 6 }}>
                <Space wrap>
                  <Button size="small" type="primary" loading={salvando} onClick={anexarNoExistente}>
                    Anexar a monografia ao marco existente
                  </Button>
                  {atual.duplicado.motivo === 'coordenada' ? (
                    <Button size="small" loading={salvando}
                      onClick={() => salvar(true)}>
                      São marcos diferentes — criar assim mesmo
                    </Button>
                  ) : (
                    <Button size="small" onClick={() => alterar(atual.uid, { duplicado: undefined })}>
                      Mudar o código e criar outro
                    </Button>
                  )}
                </Space>
              </div>
            </>} />
        )}

        {/* Conferência do arquivo atual */}
        {atual && (
          <>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Conferindo <b>{atual.nome}</b>
              {trabalhando && <> · <Spin size="small" /> outros arquivos ainda sendo lidos</>}
            </Text>
            {!atual.anexo && (
              <Alert type="warning" showIcon
                message="Este arquivo não chegou ao acervo"
                description="Dá para criar o marco assim mesmo, mas a monografia NÃO ficará anexada. Se o documento importa, feche e envie de novo." />
            )}
            <FormMarco form={form} contratos={contratos} />
            <Space style={{ justifyContent: 'flex-end', display: 'flex' }}>
              <Button onClick={adiar} disabled={salvando}>Pular por ora</Button>
              {/* 🚨 `onClick={salvar}` passaria o EVENTO do clique como
                  primeiro argumento — e `confirmarProximo` viraria truthy,
                  desligando a checagem de coordenada em todo salvamento. */}
              <Button type="primary" loading={salvando} onClick={() => salvar()}>
                Salvar marco{itens.some((i) => i.etapa === 'pronto' && i.uid !== atual.uid) ? ' e próximo' : ''}
              </Button>
            </Space>
          </>
        )}

        {!atual && itens.length > 0 && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Text>
              {salvos} monografia(s) importada(s).
              {itens.some((i) => i.etapa === 'adiado') && ' Clique numa etiqueta "adiado" para retomar.'}
            </Text>
            {trabalhando && <Text type="secondary"><Spin size="small" /> ainda lendo os demais…</Text>}
            <Space>
              <Button onClick={() => {
                // loteRef também: sem zerar, soltar OS MESMOS arquivos de novo
                // (o caso de "deu erro, vou repetir") seria ignorado em silêncio.
                loteRef.current = null;
                setItens([]); setAtualUid(null); form.resetFields();
              }}>
                Enviar outras monografias
              </Button>
              <Button type="primary" onClick={fechar}>Fechar</Button>
            </Space>
          </Space>
        )}
      </Space>
    </Modal>
  );
}
